import { CallIntelligenceService } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { MockAPIRouter } from '@frontend/fetch';
import { serviceName } from '../../service';
import { MockCallRecords } from '../call-recordings';
import { MockCallFollowUps } from '../follow-up';
import {
  extractCallOverviewFilterFromRequest,
  generatePreviousOverview,
  getFilteredOverviewData,
  MockOverviewData,
} from '../overview-data';
import { MockPersons } from '../persons';
import { BaseMockRouterContext, FeatureMockRouter } from './types';

export interface CallIntelOverviewMockRouterContext extends BaseMockRouterContext {
  mockPersonsInstance: MockPersons;
  mockCallRecordsInstance: MockCallRecords;
  mockFollowUpsInstance: MockCallFollowUps;
}

export const CallIntelOverviewMockRouter: FeatureMockRouter<CallIntelOverviewMockRouterContext> = {
  addMockRouterEndpoints: async (router, { vertical }) => {
    router.schemaEndpoint(CallIntelligenceService.GetCallsOverview, async (req, _, next) => {
      const request: CallIntelTypes.GetCallsOverviewIO['input'] = await req.clone().json();
      const filter = extractCallOverviewFilterFromRequest(request);

      // Do not mock the call overview if isCallTrendDemo is not empty
      // This is already handled by the real API in request interceptor
      if (request.isCallTrendDemo !== undefined) {
        next();
        return null;
      }

      return MockAPIRouter.json(getFilteredOverviewData(MockOverviewData, filter, vertical));
    });

    router.schemaEndpoint(CallIntelligenceService.OverviewStats, async (req) => {
      const request: CallIntelTypes.GetOverviewStatsIO['input'] = await req.clone().json();
      const filter = extractCallOverviewFilterFromRequest(request);
      return MockAPIRouter.json(getFilteredOverviewData(generatePreviousOverview(), filter, vertical));
    });
  },
  getResponseInterceptors: ({ interceptorIdPrefix, mockFollowUpsInstance }) => [
    {
      id: `${interceptorIdPrefix}-call-trends-overview`,
      interceptor: async (response) => {
        if (!response.url) {
          return response;
        }
        const { pathname } = new URL(response.url);
        if (pathname !== '/call-intelligence/v2/overview') {
          return response;
        }
        const responseBody: CallIntelTypes.GetCallsOverviewIO['output'] = await response.clone().json();
        // Add task type counts from followup to the response
        responseBody.taskTypes = mockFollowUpsInstance.followUps.reduce((acc, followUp) => {
          followUp.calls.forEach((call) => {
            call?.taskTypes?.forEach((taskType) => {
              if (!acc[taskType]) {
                acc[taskType] = 1;
              } else {
                acc[taskType] += 1;
              }
            });
          });
          return acc;
        }, {} as Record<string, number>);

        return new Response(JSON.stringify(responseBody), response);
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([serviceName, 'GetCallsOverview']);
  },
};
